import { Box, Tooltip, Typography } from "@mui/material";
import KeyboardDoubleArrowUpIcon from "@mui/icons-material/KeyboardDoubleArrowUp";
import KeyboardDoubleArrowDownIcon from "@mui/icons-material/KeyboardDoubleArrowDown";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/ar";
import "dayjs/locale/fr";
import "dayjs/locale/ur";
import { ReactComponent as TrendUp } from "assets/trend-up.svg";
import { ReactComponent as TrendDown } from "assets/trend-down.svg";

interface ComparisonChangeProps {
  percentage?: number | null;
  range?: string | null;
  /** Flip colors (e.g. cost: up = red, down = green) */
  invertColors?: boolean;
  compact?: boolean;
  /** Slightly smaller icon + text for dense widgets */
  dense?: boolean;
  /** Double-chevron + percent for metric highlight cards */
  variant?: "default" | "metric";
}

const COMPARISON_PERIOD_KEYS: Record<string, string> = {
  daily: "comparisonPeriodYesterday",
  yesterday: "comparisonPeriodPreviousDay",
  weekly: "comparisonPeriodPreviousWeek",
  monthly: "comparisonPeriodPreviousMonth",
  yearly: "comparisonPeriodPreviousYear",
};

const DAYJS_LOCALE: Record<string, string> = {
  en: "en",
  ar: "ar",
  fr: "fr",
  ur: "ur",
};

export function getComparisonPeriodKey(range?: string | null) {
  if (!range) return "comparisonPeriodPreviousPeriod";
  if (range === "last_month") return "comparisonPeriodLastMonthName";
  return COMPARISON_PERIOD_KEYS[range] || "comparisonPeriodPreviousPeriod";
}

/** Label for the compared-against period based on selected range. */
export function getComparisonPeriodLabel(
  range: string | null | undefined,
  t: (key: string) => string,
  language?: string,
) {
  if (range === "last_month") {
    // Filter "Last Month" = previous calendar month; compare against the month before that.
    const locale = DAYJS_LOCALE[language || "en"] || "en";
    return dayjs().subtract(2, "month").locale(locale).format("MMMM");
  }
  return t(getComparisonPeriodKey(range));
}

export default function ComparisonChange({
  percentage,
  range,
  invertColors = false,
  dense = false,
  variant = "default",
}: ComparisonChangeProps) {
  const { t, i18n } = useTranslation();

  if (
    percentage === null ||
    percentage === undefined ||
    Number.isNaN(Number(percentage))
  ) {
    return null;
  }

  const value = Number(percentage);
  const isUp = value >= 0;
  const isPositiveTone = invertColors ? !isUp : isUp;
  const color = isPositiveTone ? "#22C55E" : "#FF5630";
  const metricIconColor = isPositiveTone ? "#86E8AB" : "#FFAC82";
  const softBg = isPositiveTone
    ? "rgba(34, 197, 94, 0.16)"
    : "rgba(255, 86, 48, 0.16)";
  const absPct = Math.abs(value).toFixed(2);
  const signedPct = `${isUp ? "+" : "-"}${absPct}%`;
  const periodLabel = getComparisonPeriodLabel(range, t, i18n.language);
  const directionLabel = isUp ? t("increased") : t("decreased");
  const fullTitle = t("comparisonChangeTitle", {
    direction: directionLabel,
    percent: absPct,
    period: periodLabel,
  });

  if (variant === "metric") {
    return (
      <Tooltip title={fullTitle} enterDelay={150} placement="top" arrow>
        <Box
          className="inline-flex items-center gap-0.5 min-w-0"
          sx={{ flexWrap: "wrap", justifyContent: "flex-start" }}
        >
          {isUp ? (
            <KeyboardDoubleArrowUpIcon
              sx={{ color: metricIconColor, fontSize: 17 }}
            />
          ) : (
            <KeyboardDoubleArrowDownIcon
              sx={{ color: metricIconColor, fontSize: 17 }}
            />
          )}
          <Typography
            className="font-Saira"
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: "#637381",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {signedPct}
          </Typography>
          <Typography
            className="font-Saira"
            sx={{
              fontSize: 10,
              fontWeight: 400,
              color: "#9CA3AF",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              maxWidth: { xs: 110, sm: 140, md: 160 },
            }}
          >
            {periodLabel}
          </Typography>
        </Box>
      </Tooltip>
    );
  }

  const iconBox = dense ? 26 : 32;
  const iconSize = dense ? 16 : 20;

  return (
    <Tooltip title={fullTitle} enterDelay={150} placement="top" arrow>
      <Box
        className="inline-flex items-center gap-1.5 min-w-0"
        sx={{ flexWrap: { xs: "wrap", sm: "nowrap" } }}
      >
        <Box
          className="flex items-center justify-center rounded-full"
          sx={{
            width: iconBox,
            height: iconBox,
            backgroundColor: softBg,
            color,
            flexShrink: 0,
          }}
        >
          {isUp ? (
            <TrendUp style={{ width: iconSize, height: iconSize }} />
          ) : (
            <TrendDown style={{ width: iconSize, height: iconSize }} />
          )}
        </Box>

        <Typography
          className="font-Saira"
          sx={{
            fontSize: dense ? 12 : 13,
            lineHeight: 1.2,
            color: "#374151",
            fontWeight: 700,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {signedPct}
        </Typography>

        <Typography
          className="font-Saira"
          sx={{
            fontSize: dense ? 11 : 12,
            lineHeight: 1.2,
            color: "#9CA3AF",
            fontWeight: 400,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: { xs: 120, sm: 160, md: 180 },
          }}
        >
          {periodLabel}
        </Typography>
      </Box>
    </Tooltip>
  );
}
